import { useCallback, useEffect, useState, useRef } from "react";
import { useTheme } from "@mui/material";
import { toast } from "@vilocnv/allsetra-core";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectTripBackupReminderStatus } from "app/data/selectors/tripBackupReminderSelectors";
import {
  checkShouldShowReminderThunk,
  dismissReminderThunk
} from "app/features/trip-backup-reminder/tripBackupReminderActions";
import AuditService from "app/data/services/AuditService";
import { TripBackupReminderCache } from "app/data/utils/TripBackupReminderCache";

export const useTripBackupReminder = (accountId: string) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const reminderStatus = useAppSelector(selectTripBackupReminderStatus);
  const hasCheckedRef = useRef(false);
  const lastAccountIdRef = useRef<string | null>(null);

  const fetchAndCheckReminder = useCallback(async () => {
    if (!accountId) return;

    if (hasCheckedRef.current && lastAccountIdRef.current === accountId) {
      return;
    }

    hasCheckedRef.current = true;
    lastAccountIdRef.current = accountId;

    try {
      await dispatch(checkShouldShowReminderThunk(accountId)).unwrap();
    } catch (error) {
      console.error("Error fetching reminder status:", error);
    }
  }, [dispatch, accountId]);

  const logAuditAction = useCallback(
    async (action: "dismiss") => {
      try {
        await AuditService.logTripBackupAction({
          accountId,
          action,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error("Failed to log audit action:", error);
      }
    },
    [accountId]
  );

  const handleDismiss = useCallback(async () => {
    if (!accountId) return;

    try {
      await dispatch(dismissReminderThunk(accountId)).unwrap();
      logAuditAction("dismiss");
      setOpen(false);
      toast.info(
        "You'll be reminded to backup your trip registration data in three months"
      );
    } catch (error) {
      // If API fails, save to localStorage as fallback
      console.warn(
        "Failed to dismiss via API, saving to localStorage as fallback:",
        error
      );
      TripBackupReminderCache.saveAction(accountId, "dismiss");
      logAuditAction("dismiss");
      setOpen(false);
      toast.info(
        "You'll be reminded to backup your trip registration data in three months"
      );
    }
  }, [accountId, dispatch, logAuditAction]);

  useEffect(() => {
    if (lastAccountIdRef.current && lastAccountIdRef.current !== accountId) {
      hasCheckedRef.current = false;
    }
  }, [accountId]);

  useEffect(() => {
    fetchAndCheckReminder();
  }, [fetchAndCheckReminder]);

  useEffect(() => {
    if (
      reminderStatus.shouldShow &&
      !reminderStatus.loading &&
      !reminderStatus.error
    ) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [reminderStatus.shouldShow, reminderStatus.loading, reminderStatus.error]);

  return {
    open,
    theme,
    handleDismiss
  };
};
